import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";

import axios from "axios";

import Cookies from "js-cookie";

import { io } from "socket.io-client";

import { toast } from "react-toastify";

import { crmHeaders, crmMultipartHeaders } from "../utils/crmApi";

import { playNotificationSound } from "../utils/playNotificationSound";



const BASE_URL = process.env.REACT_APP_BASE_URL;

const SOCKET_URL = process.env.REACT_APP_BASE_URL || "http://localhost:4001";



const TeamChatContext = createContext(null);



function getInitials(name) {

    if (!name) return "?";

    return name.substring(0, 2).toUpperCase();

}



function formatPreview(msg) {

    if (!msg) return "No messages yet";

    if (msg.Message_Type === "file") return `📎 ${msg.File_Name || "File"}`;

    if (msg.Message_Type === "voice") return "🎤 Voice message";

    if (msg.Message_Type === "system") return msg.Body;

    return msg.Body || "";

}



function parseMentions(text) {

    const parts = String(text || "").split(/(@[A-Za-z0-9_.\- ]+)/g);

    return parts.map((part, i) => {

        if (part.startsWith("@")) {

            return (

                <span key={i} className="team-chat-mention">

                    {part}

                </span>

            );

        }

        return part;

    });

}



function computeMessageStatus(msg, readStates, members, username, convType) {

    if (msg.Sender_Name !== username || msg.Message_Type === "system") return null;

    if (msg._status === "sending") return "sending";

    if (msg._status === "failed") return "failed";



    const others = (members || []).filter((m) => m !== username);

    if (!others.length) return "sent";



    const seenBy = others.filter((u) => {

        const rs = (readStates || []).find((r) => r.User_Name === u);

        return rs && rs.Last_Read_Message_Id >= msg.Id;

    });



    if (seenBy.length === others.length) return "seen";

    if (seenBy.length > 0 && convType === "group") return "seen";

    if (seenBy.length > 0 && convType === "direct") return "seen";

    if (msg._status === "delivered" || msg._deliveredTo?.length) return "delivered";

    return "sent";

}



function applyStatusToMessages(messages, readStates, members, username, convType) {

    return messages.map((m) => ({

        ...m,

        _status: computeMessageStatus(m, readStates, members, username, convType),

    }));

}



function upsertMessage(list, message) {

    const idx = list.findIndex(

        (m) => m.Id === message.Id || (message.Client_Temp_Id && m.Client_Temp_Id === message.Client_Temp_Id)

    );

    if (idx >= 0) {

        const next = [...list];

        next[idx] = { ...next[idx], ...message };

        return next;

    }

    if (list.some((m) => m.Id === message.Id)) return list;

    return [...list, message];

}



function sumUnread(list) {

    return (list || []).reduce((s, c) => s + (c.unreadCount || 0), 0);

}



function ChatToastContent({ batch, convName, totalUnread }) {

    const count = batch.count;

    return (

        <div className="team-chat-toast">

            <div className="team-chat-toast-count">{count > 99 ? "99+" : count}</div>

            <div className="team-chat-toast-body">

                <strong>

                    {count > 1 ? `${count} new messages` : "New message"}

                    {convName ? ` · ${convName}` : ""}

                </strong>

                <span className="team-chat-toast-from">From {batch.from}</span>

                <span className="team-chat-toast-preview">{formatPreview(batch.lastMessage)}</span>

                <small className="team-chat-toast-total">

                    {totalUnread} unread message{totalUnread !== 1 ? "s" : ""} total

                </small>

            </div>

        </div>

    );

}



export function TeamChatProvider({ children }) {

    const username = Cookies.get("username") || "";

    const [open, setOpen] = useState(false);

    const [view, setView] = useState("list");

    const [tab, setTab] = useState("all");

    const [conversations, setConversations] = useState([]);

    const [activeConversation, setActiveConversation] = useState(null);

    const [messages, setMessages] = useState([]);

    const [readStates, setReadStates] = useState([]);

    const [allUsers, setAllUsers] = useState([]);

    const [onlineUsers, setOnlineUsers] = useState([]);

    const [typingUsers, setTypingUsers] = useState({});

    const [totalUnread, setTotalUnread] = useState(0);

    const [loadingMessages, setLoadingMessages] = useState(false);

    const [showGroupModal, setShowGroupModal] = useState(false);

    const [incomingCall, setIncomingCall] = useState(null);

    const [activeCall, setActiveCall] = useState(null);

    const [recentNotif, setRecentNotif] = useState(null);



    const socketRef = useRef(null);

    const messagesEndRef = useRef(null);

    const peerRef = useRef(null);

    const localStreamRef = useRef(null);

    const typingTimerRef = useRef(null);

    const activeConvRef = useRef(null);

    const openRef = useRef(false);

    const viewRef = useRef("list");

    const conversationsRef = useRef([]);

    const seenMsgIdsRef = useRef(new Set());

    const notifBatchRef = useRef({});

    const notifTimerRef = useRef(null);



    useEffect(() => {

        activeConvRef.current = activeConversation?.id ?? null;

    }, [activeConversation?.id]);



    useEffect(() => {

        openRef.current = open;

        if (open) setRecentNotif(null);

    }, [open]);



    useEffect(() => {

        viewRef.current = view;

    }, [view]);



    useEffect(() => {

        if (!recentNotif || open) return undefined;

        const timer = setTimeout(() => setRecentNotif(null), 9000);

        return () => clearTimeout(timer);

    }, [recentNotif, open]);



    useEffect(() => {

        const base = "CRM Portal";

        document.title = totalUnread > 0 ? `(${totalUnread > 99 ? "99+" : totalUnread}) ${base}` : base;

        return () => {

            document.title = base;

        };

    }, [totalUnread]);



    useEffect(() => {

        conversationsRef.current = conversations;

    }, [conversations]);



    const refreshConversations = useCallback(async () => {

        if (!username) return;

        try {

            const res = await axios.get(`${BASE_URL}/chat/conversations`, { headers: crmHeaders() });

            setConversations(res.data.conversations || []);

            setOnlineUsers(res.data.online || []);

            const unread = (res.data.conversations || []).reduce((s, c) => s + (c.unreadCount || 0), 0);

            setTotalUnread(unread);

        } catch {

            /* ignore */

        }

    }, [username]);



    const loadUsers = useCallback(async () => {

        try {

            const res = await axios.get(`${BASE_URL}/getusers`, { headers: crmHeaders() });

            setAllUsers((res.data || []).filter((u) => u.Name !== username));

        } catch {

            /* ignore */

        }

    }, [username]);



    const loadMessages = useCallback(

        async (convId, convMeta) => {

            if (!convId) return;

            setLoadingMessages(true);

            try {

                const res = await axios.get(`${BASE_URL}/chat/conversations/${convId}/messages`, {

                    headers: crmHeaders(),

                });

                const loaded = res.data.messages || [];

                const states = res.data.readStates || [];

                const members = res.data.members || convMeta?.members || [];

                setReadStates(states);

                setMessages(

                    applyStatusToMessages(loaded, states, members, username, convMeta?.type || "direct")

                );

                const last = loaded[loaded.length - 1];

                if (last && socketRef.current) {

                    socketRef.current.emit("chat:read", { conversationId: convId, messageId: last.Id });

                }

            } catch {

                toast.error("Could not load messages");

            } finally {

                setLoadingMessages(false);

            }

        },

        [username]

    );



    const openChatFromNotifRef = useRef(() => {});

    const flushChatNotificationsRef = useRef(() => {});

    const queueChatNotificationRef = useRef(() => {});

    const processIncomingMessageRef = useRef(() => {});



    openChatFromNotifRef.current = (conversationId) => {

        setOpen(true);

        const conv = conversationsRef.current.find((c) => c.id === conversationId);

        if (conv) {

            setActiveConversation(conv);

            setView("chat");

            loadMessages(conversationId, conv);

        } else {

            refreshConversations();

        }

        setRecentNotif(null);

    };



    flushChatNotificationsRef.current = () => {

        const batches = { ...notifBatchRef.current };

        notifBatchRef.current = {};

        notifTimerRef.current = null;

        const entries = Object.entries(batches);

        if (!entries.length) return;

        const convs = conversationsRef.current;

        const total = sumUnread(convs);

        let totalNew = 0;

        entries.forEach(([, batch]) => {

            totalNew += batch.count;

        });

        const batchDetails = entries.map(([convId, batch]) => ({

            conversationId: parseInt(convId, 10),

            ...batch,

            convName: convs.find((c) => c.id === parseInt(convId, 10))?.name || "Chat",

        }));

        setRecentNotif({

            totalNew,

            totalUnread: total,

            batches: batchDetails,

            at: Date.now(),

        });

        if (entries.length === 1) {

            const [convId, batch] = entries[0];

            const convName = convs.find((c) => c.id === parseInt(convId, 10))?.name || "Chat";

            toast.info(<ChatToastContent batch={batch} convName={convName} totalUnread={total} />, {

                autoClose: 7000,

                icon: false,

                onClick: () => openChatFromNotifRef.current(parseInt(convId, 10)),

            });

        } else {

            toast.info(

                <div className="team-chat-toast">

                    <div className="team-chat-toast-count">{totalNew > 99 ? "99+" : totalNew}</div>

                    <div className="team-chat-toast-body">

                        <strong>

                            {totalNew} new messages in {entries.length} chats

                        </strong>

                        <small className="team-chat-toast-total">

                            {total} unread message{total !== 1 ? "s" : ""} total

                        </small>

                    </div>

                </div>,

                {

                    autoClose: 7000,

                    icon: false,

                    onClick: () => setOpen(true),

                }

            );

        }

    };



    queueChatNotificationRef.current = (conversationId, message, from) => {

        const key = String(conversationId);

        const prev = notifBatchRef.current[key];

        notifBatchRef.current[key] = {

            count: (prev?.count || 0) + 1,

            from: from || message?.Sender_Name || "Someone",

            lastMessage: message,

        };

        clearTimeout(notifTimerRef.current);

        notifTimerRef.current = setTimeout(() => flushChatNotificationsRef.current(), 650);

    };



    processIncomingMessageRef.current = (conversationId, message, from, socket) => {

        if (!message || message.Sender_Name === username) return;

        if (message.Id && seenMsgIdsRef.current.has(message.Id)) return;

        if (message.Id) {

            seenMsgIdsRef.current.add(message.Id);

            if (seenMsgIdsRef.current.size > 300) {

                const ids = [...seenMsgIdsRef.current];

                seenMsgIdsRef.current = new Set(ids.slice(-150));

            }

        }

        const viewingChat =

            activeConvRef.current === conversationId &&

            openRef.current &&

            viewRef.current === "chat";

        setConversations((prev) => {

            const updated = prev.map((c) =>

                c.id === conversationId

                    ? {

                          ...c,

                          lastMessage: message,

                          unreadCount: viewingChat ? 0 : (c.unreadCount || 0) + 1,

                      }

                    : c

            );

            const sorted = updated.sort(

                (a, b) =>

                    new Date(b.lastMessage?.Created_At || b.createdAt) -

                    new Date(a.lastMessage?.Created_At || a.createdAt)

            );

            setTotalUnread(sumUnread(sorted));

            conversationsRef.current = sorted;

            return sorted;

        });

        if (activeConvRef.current === conversationId) {

            setMessages((prev) => {

                const merged = upsertMessage(prev, {

                    ...message,

                    _status: message.Sender_Name === username ? "sent" : undefined,

                });

                return applyStatusToMessages(

                    merged,

                    readStates,

                    activeConversation?.members,

                    username,

                    activeConversation?.type

                );

            });

        }

        if (viewingChat) {

            socket.emit("chat:read", { conversationId, messageId: message.Id });

            return;

        }

        playNotificationSound();

        queueChatNotificationRef.current(conversationId, message, from || message.Sender_Name);

    };



    useEffect(() => {

        if (!username) return undefined;



        refreshConversations();

        loadUsers();



        const socket = io(SOCKET_URL, {

            auth: {

                apiKey: process.env.REACT_APP_API_KEY,

                username,

            },

            transports: ["websocket", "polling"],

        });

        socketRef.current = socket;



        socket.on("chat:presence", ({ online }) => setOnlineUsers(online || []));



        socket.on("chat:message", ({ conversationId, message }) => {

            processIncomingMessageRef.current(conversationId, message, message.Sender_Name, socket);

        });



        socket.on("chat:status", ({ conversationId, messageId, status, seenBy, deliveredTo }) => {

            if (activeConvRef.current !== conversationId) return;

            setMessages((prev) =>

                prev.map((m) => {

                    if (m.Sender_Name !== username || m.Message_Type === "system") return m;

                    if (status === "delivered" && m.Id === messageId) {

                        return { ...m, _status: "delivered", _deliveredTo: deliveredTo || [] };

                    }

                    if (status === "seen" && typeof m.Id === "number" && m.Id <= messageId) {

                        const seenList = [...new Set([...(m._seenBy || []), seenBy].filter(Boolean))];

                        return { ...m, _status: "seen", _seenBy: seenList };

                    }

                    return m;

                })

            );

            if (status === "seen" && seenBy && seenBy !== username) {

                setReadStates((prev) => {

                    const existing = prev.find((r) => r.User_Name === seenBy);

                    if (existing && existing.Last_Read_Message_Id >= messageId) return prev;

                    const rest = prev.filter((r) => r.User_Name !== seenBy);

                    return [...rest, { User_Name: seenBy, Last_Read_Message_Id: messageId }];

                });

            }

        });



        socket.on("chat:notify", ({ conversationId, message, from }) => {

            processIncomingMessageRef.current(conversationId, message, from, socket);

        });



        socket.on("chat:mention", ({ from, message }) => {

            playNotificationSound();

            toast.warning(`${from} mentioned you: ${formatPreview(message)}`);

        });



        socket.on("chat:typing", ({ conversationId, username: u, isTyping }) => {

            if (activeConvRef.current !== conversationId) return;

            setTypingUsers((prev) => {

                const next = { ...prev };

                if (isTyping) next[u] = true;

                else delete next[u];

                return next;

            });

        });



        socket.on("call:incoming", (payload) => {

            playNotificationSound();

            setIncomingCall(payload);

        });



        socket.on("call:accepted", async ({ answer }) => {

            if (peerRef.current) {

                await peerRef.current.setRemoteDescription(new RTCSessionDescription(answer));

            }

        });



        socket.on("call:rejected", () => {

            endCall();

            toast.info("Call declined");

        });



        socket.on("call:ended", () => {

            endCall();

        });



        socket.on("call:ice", async ({ candidate }) => {

            if (peerRef.current && candidate) {

                try {

                    await peerRef.current.addIceCandidate(new RTCIceCandidate(candidate));

                } catch {

                    /* ignore */

                }

            }

        });



        return () => {

            socket.disconnect();

            socketRef.current = null;

        };

    }, [username, loadMessages, refreshConversations]); // eslint-disable-line react-hooks/exhaustive-deps



    useEffect(() => {

        if (activeConversation?.id && socketRef.current) {

            socketRef.current.emit("chat:join_conversation", activeConversation.id);

            loadMessages(activeConversation.id, activeConversation);

        }

    }, [activeConversation?.id, loadMessages]);



    useEffect(() => {

        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });

    }, [messages, typingUsers]);



    useEffect(() => {

        const unread = conversations.reduce((s, c) => s + (c.unreadCount || 0), 0);

        setTotalUnread(unread);

    }, [conversations]);



    const startDirectChat = async (targetUser) => {

        try {

            const res = await axios.post(

                `${BASE_URL}/chat/conversations/direct`,

                { targetUser },

                { headers: crmHeaders() }

            );

            const conv = res.data.conversation;

            setConversations((prev) => {

                const exists = prev.find((c) => c.id === conv.id);

                if (exists) return prev.map((c) => (c.id === conv.id ? conv : c));

                return [conv, ...prev];

            });

            setActiveConversation(conv);

            setView("chat");

        } catch (err) {

            toast.error(err.response?.data?.message || "Could not start chat");

        }

    };



    const createGroup = async (name, members) => {

        try {

            const res = await axios.post(

                `${BASE_URL}/chat/conversations/group`,

                { name, members },

                { headers: crmHeaders() }

            );

            const conv = res.data.conversation;

            setConversations((prev) => [conv, ...prev]);

            setActiveConversation(conv);

            setView("chat");

            setShowGroupModal(false);

        } catch (err) {

            toast.error(err.response?.data?.message || "Could not create group");

        }

    };



    const sendMessage = useCallback(

        async (body, messageType = "text", fileUrl = null, fileName = null) => {

            if (!activeConversation?.id || !socketRef.current) return false;

            if (messageType === "text" && !body.trim()) return false;



            const tempId = `temp-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;

            const optimistic = {

                Id: tempId,

                Client_Temp_Id: tempId,

                Conversation_Id: activeConversation.id,

                Sender_Name: username,

                Body: body,

                Message_Type: messageType,

                File_Url: fileUrl,

                File_Name: fileName,

                Created_At: new Date().toISOString(),

                _status: "sending",

            };



            setMessages((prev) => [...prev, optimistic]);

            setConversations((prev) =>

                prev.map((c) =>

                    c.id === activeConversation.id

                        ? { ...c, lastMessage: optimistic, unreadCount: 0 }

                        : c

                )

            );



            return new Promise((resolve) => {

                socketRef.current.emit(

                    "chat:send",

                    {

                        conversationId: activeConversation.id,

                        body,

                        messageType,

                        fileUrl,

                        fileName,

                    },

                    (res) => {

                        if (res?.ok && res.message) {

                            setMessages((prev) => {
                                const withoutTemp = prev.filter(
                                    (m) => m.Client_Temp_Id !== tempId && m.Id !== tempId
                                );
                                const replaced = upsertMessage(withoutTemp, {
                                    ...res.message,
                                    _status: "sent",
                                });
                                return applyStatusToMessages(
                                    replaced,
                                    readStates,
                                    activeConversation.members,
                                    username,
                                    activeConversation.type
                                );
                            });

                            setConversations((prev) =>

                                prev.map((c) =>

                                    c.id === activeConversation.id

                                        ? { ...c, lastMessage: res.message }

                                        : c

                                )

                            );

                            resolve(true);

                        } else {

                            setMessages((prev) =>

                                prev.map((m) =>

                                    m.Client_Temp_Id === tempId ? { ...m, _status: "failed" } : m

                                )

                            );

                            toast.error(res?.message || "Send failed");

                            resolve(false);

                        }

                    }

                );

            });

        },

        [activeConversation, readStates, username]

    );



    const uploadFile = async (file) => {

        const form = new FormData();

        form.append("file", file);

        const res = await axios.post(`${BASE_URL}/chat/upload`, form, {

            headers: crmMultipartHeaders(),

        });

        return res.data;

    };



    const emitTyping = (isTyping) => {

        if (!activeConversation?.id || !socketRef.current) return;

        socketRef.current.emit("chat:typing", {

            conversationId: activeConversation.id,

            isTyping,

        });

    };



    const handleInputChange = (value, setInput) => {

        setInput(value);

        emitTyping(true);

        clearTimeout(typingTimerRef.current);

        typingTimerRef.current = setTimeout(() => emitTyping(false), 1200);

    };



    const endCall = useCallback(() => {

        localStreamRef.current?.getTracks().forEach((t) => t.stop());

        localStreamRef.current = null;

        peerRef.current?.close();

        peerRef.current = null;

        setActiveCall(null);

        setIncomingCall(null);

    }, []);



    const createPeerConnection = (targetUser) => {

        const pc = new RTCPeerConnection({

            iceServers: [{ urls: "stun:stun.l.google.com:19302" }],

        });

        pc.onicecandidate = (e) => {

            if (e.candidate && socketRef.current) {

                socketRef.current.emit("call:ice", { targetUser, candidate: e.candidate });

            }

        };

        pc.ontrack = (e) => {

            const audio = document.getElementById("team-chat-remote-audio");

            if (audio) audio.srcObject = e.streams[0];

        };

        return pc;

    };



    const startCall = async (callType = "audio") => {

        if (!activeConversation) return;

        const other =

            activeConversation.type === "direct"

                ? activeConversation.members.find((m) => m !== username)

                : null;

        if (!other) {

            toast.info("Select a direct chat for calls");

            return;

        }



        try {

            const stream = await navigator.mediaDevices.getUserMedia({

                audio: true,

                video: callType === "video",

            });

            localStreamRef.current = stream;

            const pc = createPeerConnection(other);

            peerRef.current = pc;

            stream.getTracks().forEach((track) => pc.addTrack(track, stream));



            const offer = await pc.createOffer();

            await pc.setLocalDescription(offer);



            socketRef.current?.emit("call:invite", {

                targetUser: other,

                conversationId: activeConversation.id,

                callType,

                offer,

            });



            setActiveCall({ with: other, type: callType, outgoing: true });

        } catch {

            toast.error("Microphone/camera access denied");

        }

    };



    const acceptCall = async () => {

        if (!incomingCall) return;

        try {

            const stream = await navigator.mediaDevices.getUserMedia({

                audio: true,

                video: incomingCall.callType === "video",

            });

            localStreamRef.current = stream;

            const pc = createPeerConnection(incomingCall.from);

            peerRef.current = pc;

            stream.getTracks().forEach((track) => pc.addTrack(track, stream));



            await pc.setRemoteDescription(new RTCSessionDescription(incomingCall.offer));

            const answer = await pc.createAnswer();

            await pc.setLocalDescription(answer);



            socketRef.current?.emit("call:accept", {

                targetUser: incomingCall.from,

                callId: incomingCall.callId,

                answer,

            });



            setActiveCall({

                with: incomingCall.from,

                type: incomingCall.callType,

                outgoing: false,

            });

            setIncomingCall(null);

        } catch {

            toast.error("Could not accept call");

            rejectCall();

        }

    };



    const rejectCall = () => {

        if (incomingCall) {

            socketRef.current?.emit("call:reject", {

                targetUser: incomingCall.from,

                callId: incomingCall.callId,

            });

        }

        setIncomingCall(null);

    };



    const hangUp = () => {

        if (activeCall?.with) {

            socketRef.current?.emit("call:end", {

                targetUser: activeCall.with,

                callId: incomingCall?.callId,

            });

        }

        endCall();

    };



    const getMessageStatus = useCallback(

        (msg) => computeMessageStatus(msg, readStates, activeConversation?.members, username, activeConversation?.type),

        [readStates, activeConversation, username]

    );



    const filteredConversations = useMemo(() => {

        if (tab === "direct") return conversations.filter((c) => c.type === "direct");

        if (tab === "groups") return conversations.filter((c) => c.type === "group");

        return conversations;

    }, [conversations, tab]);



    const value = useMemo(

        () => ({

            open,

            setOpen,

            view,

            setView,

            tab,

            setTab,

            conversations: filteredConversations,

            allConversations: conversations,

            activeConversation,

            setActiveConversation,

            messages,

            allUsers,

            onlineUsers,

            typingUsers,

            totalUnread,

            recentNotif,

            loadingMessages,

            showGroupModal,

            setShowGroupModal,

            incomingCall,

            activeCall,

            refreshConversations,

            startDirectChat,

            createGroup,

            sendMessage,

            uploadFile,

            handleInputChange,

            startCall,

            acceptCall,

            rejectCall,

            hangUp,

            getInitials,

            formatPreview,

            parseMentions,

            getMessageStatus,

            username,

            messagesEndRef,

        }),

        [

            open,

            view,

            tab,

            filteredConversations,

            conversations,

            activeConversation,

            messages,

            allUsers,

            onlineUsers,

            typingUsers,

            totalUnread,

            recentNotif,

            loadingMessages,

            showGroupModal,

            incomingCall,

            activeCall,

            refreshConversations,

            startDirectChat,

            createGroup,

            sendMessage,

            uploadFile,

            handleInputChange,

            startCall,

            acceptCall,

            rejectCall,

            hangUp,

            getMessageStatus,

            username,

        ]

    );



    return <TeamChatContext.Provider value={value}>{children}</TeamChatContext.Provider>;

}



export function useTeamChat() {

    const ctx = useContext(TeamChatContext);

    if (!ctx) throw new Error("useTeamChat must be used within TeamChatProvider");

    return ctx;

}



export function getMessageStatusLabel(status) {

    switch (status) {

        case "sending":

            return "Sending…";

        case "sent":

            return "Sent";

        case "delivered":

            return "Delivered";

        case "seen":

            return "Seen";

        case "failed":

            return "Failed";

        default:

            return "";

    }

}


